// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {loadPostPreview} from '@app/features/feed/commands/FeedPostPreviewCommands';
import styles from '@app/features/feed/components/FeedPostEmbed.module.css';
import FeedPostPreviews from '@app/features/feed/state/FeedPostPreviews';
import {
	EmbedCard,
	EmbedSkeletonButton,
	EmbedSkeletonCircle,
	EmbedSkeletonSubtitle,
	EmbedSkeletonTitle,
} from '@app/features/messaging/components/embeds/embed_card/EmbedCard';
import cardStyles from '@app/features/messaging/components/embeds/embed_card/EmbedCard.module.css';
import {useEmbedSkeletonOverride} from '@app/features/messaging/components/embeds/embed_card/useEmbedSkeletonOverride';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {ComponentBus} from '@app/features/platform/utils/ComponentBus';
import {Button} from '@app/features/ui/button/Button';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {QuestionIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef} from 'react';

const FEED_POST_DESCRIPTOR = msg({
	message: 'Feed post',
	comment: 'Subtitle on the card of a feed post linked in a message.',
});
const OPEN_POST_DESCRIPTOR = msg({
	message: 'Open post',
	comment: 'Button on the card of a feed post linked in a message. Opens the post in the app.',
});
const POST_NOT_FOUND_DESCRIPTOR = msg({
	message: 'Post not found',
	comment: 'Title on the card of a linked feed post that was deleted or cannot be seen.',
});
const POST_NOT_FOUND_HELP_DESCRIPTOR = msg({
	message: 'It may have been deleted, or you cannot see it.',
	comment: 'Help text under "Post not found" on the card of a linked feed post.',
});
const POST_UNAVAILABLE_DESCRIPTOR = msg({
	message: 'Post unavailable',
	comment: 'Disabled button label on the card of a linked feed post that cannot be shown.',
});

// Micup: a micup.gg/feed/posts/<id> link in a message, shown the way an invite link is.
export const FeedPostEmbed = observer(function FeedPostEmbed({postId}: {postId: string}) {
	const {i18n} = useLingui();
	const slot = FeedPostPreviews.get(postId);
	const shouldForceSkeleton = useEmbedSkeletonOverride();
	useEffect(() => {
		if (!slot) void loadPostPreview(postId);
	}, [postId, slot]);
	const wasLoadingRef = useRef(true);
	const isLoading = !slot || slot.status === 'loading';
	useEffect(() => {
		if (wasLoadingRef.current && !isLoading) ComponentBus.dispatch('LAYOUT_RESIZED');
		wasLoadingRef.current = isLoading;
	}, [isLoading]);
	if (shouldForceSkeleton || !slot || slot.status === 'loading') {
		return <FeedPostLoadingState data-flx="feed.feed-post-embed.feed-post-loading-state" />;
	}
	if (slot.status === 'none') {
		return <FeedPostNotFound data-flx="feed.feed-post-embed.feed-post-not-found" />;
	}
	const {author, text, imageUrl} = slot.preview;
	return (
		<EmbedCard
			splashURL={null}
			icon={
				<img
					className={styles.avatar}
					src={AvatarUtils.getUserAvatarURL({id: author.id, avatar: author.avatar})}
					alt=""
					data-flx="feed.feed-post-embed.avatar"
				/>
			}
			title={
				<h3 className={`${cardStyles.title} ${cardStyles.titlePrimary}`} data-flx="feed.feed-post-embed.title">
					{author.global_name || author.username}
				</h3>
			}
			subtitle={
				<span className={cardStyles.helpText} data-flx="feed.feed-post-embed.subtitle">
					{i18n._(FEED_POST_DESCRIPTOR)}
				</span>
			}
			body={
				text || imageUrl ? (
					<div className={styles.body} data-flx="feed.feed-post-embed.body">
						{text && (
							<p className={styles.text} data-flx="feed.feed-post-embed.text">
								{text}
							</p>
						)}
						{imageUrl && (
							<img
								className={styles.image}
								src={imageUrl}
								alt=""
								loading="lazy"
								data-flx="feed.feed-post-embed.image"
							/>
						)}
					</div>
				) : undefined
			}
			footer={
				<Button
					variant="primary"
					fitContainer
					matchSkeletonHeight
					onClick={() => RouterUtils.transitionTo(Routes.feedPost(postId))}
					data-flx="feed.feed-post-embed.button.open"
				>
					{i18n._(OPEN_POST_DESCRIPTOR)}
				</Button>
			}
			data-flx="feed.feed-post-embed.embed-card"
		/>
	);
});

const FeedPostLoadingState = () => (
	<EmbedCard
		splashURL={null}
		icon={<EmbedSkeletonCircle data-flx="feed.feed-post-embed.loading.skeleton-circle" />}
		title={<EmbedSkeletonTitle data-flx="feed.feed-post-embed.loading.skeleton-title" />}
		body={<EmbedSkeletonSubtitle data-flx="feed.feed-post-embed.loading.skeleton-subtitle" />}
		footer={<EmbedSkeletonButton data-flx="feed.feed-post-embed.loading.skeleton-button" />}
		data-flx="feed.feed-post-embed.loading.embed-card"
	/>
);

const FeedPostNotFound = () => {
	const {i18n} = useLingui();
	return (
		<EmbedCard
			splashURL={null}
			icon={
				<div className={cardStyles.iconCircleDisabled} data-flx="feed.feed-post-embed.not-found.icon">
					<QuestionIcon className={cardStyles.iconError} data-flx="feed.feed-post-embed.not-found.question-icon" />
				</div>
			}
			title={
				<h3 className={`${cardStyles.title} ${cardStyles.titleDanger}`} data-flx="feed.feed-post-embed.not-found.title">
					{i18n._(POST_NOT_FOUND_DESCRIPTOR)}
				</h3>
			}
			subtitle={
				<span className={cardStyles.helpText} data-flx="feed.feed-post-embed.not-found.help">
					{i18n._(POST_NOT_FOUND_HELP_DESCRIPTOR)}
				</span>
			}
			footer={
				<Button
					variant="primary"
					fitContainer
					matchSkeletonHeight
					disabled
					data-flx="feed.feed-post-embed.not-found.button"
				>
					{i18n._(POST_UNAVAILABLE_DESCRIPTOR)}
				</Button>
			}
			data-flx="feed.feed-post-embed.not-found.embed-card"
		/>
	);
};
